import React from 'react';
import {
  ChipSelector, FormField, JalaliDatePicker, TextInput, Textarea, splitDateTime, useToast,
} from '@parsina/framework';

import { useTranslation } from '../../i18n';
import TaskDialog from '../../components/TaskDialog';
import {
  FOLLOW_UP_PRESETS, FOLLOW_UP_TITLE_MAX, NOTE_MAX, followUpPresetKey,
} from '../../constants/leadPipeline';
import { crmErrorMessage, crmGql } from '../../lib/crmApi';
import { CREATE_FOLLOW_UP } from './queries';

const CUSTOM = 'custom';

/**
 * A picker value ('YYYY-MM-DD HH:mm', the kit's Gregorian contract) read as
 * TEHRAN wall-clock time → epoch milliseconds, or null. The platform runs on
 * Iran time whatever the rep's browser says (§11 «Time»); +03:30 all year.
 */
export function dueAtFromPicker(value) {
  const { date, time } = splitDateTime(value);
  if (!date || !time) return null;
  const ms = new Date(`${date}T${time}:00+03:30`).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/**
 * «پیگیری جدید» — schedule the demo session or a callback (§3.2 T5).
 *
 * `crmCreateFollowUp(customerId, { leadId, assignedTo: <me>, dueAtMs })`. The
 * due time is REQUIRED (the CRM: «a follow-up without a date is a note») and
 * travels as an epoch-ms string. The picker is Jalali and read in Tehran time.
 */
export default function FollowUpDialog({
  open, onOpenChange, lead, mySub, onDone, now = () => Date.now(),
}) {
  const { t } = useTranslation();
  const toast = useToast();

  const [preset, setPreset] = React.useState(FOLLOW_UP_PRESETS[0]);
  const [custom, setCustom] = React.useState('');
  const [due, setDue] = React.useState('');
  const [note, setNote] = React.useState('');
  const [touched, setTouched] = React.useState(false);
  const [pending, setPending] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (!open) return;
    setPreset(FOLLOW_UP_PRESETS[0]); setCustom(''); setDue(''); setNote('');
    setTouched(false); setError(null);
  }, [open]);

  const title = preset === CUSTOM ? custom.trim() : t(followUpPresetKey(preset));
  const dueMs = dueAtFromPicker(due);
  const titleError = touched && !title ? t('demoRequests.followUp.titleRequired') : undefined;
  const dueError = !touched ? undefined
    : dueMs == null ? t('demoRequests.followUp.dueRequired')
      : dueMs < now() - 60_000 ? t('demoRequests.followUp.duePast') : undefined;
  const dirty = !!(custom || due || note || preset !== FOLLOW_UP_PRESETS[0]);

  const submit = async () => {
    setTouched(true);
    if (!title || dueMs == null || dueMs < now() - 60_000 || pending) return;
    setPending(true);
    setError(null);
    try {
      await crmGql(CREATE_FOLLOW_UP, {
        customerId: lead.customerId,
        input: {
          title,
          note: note.trim() || null,
          dueAtMs: String(dueMs),
          leadId: lead.id,
          assignedTo: mySub || null,
        },
      });
      toast.success(t('demoRequests.followUp.success'));
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      setError(crmErrorMessage(e, t));
    } finally {
      setPending(false);
    }
  };

  return (
    <TaskDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('demoRequests.followUp.title')}
      error={error}
      pending={pending}
      dirty={dirty}
      submitLabel={t('demoRequests.followUp.submit')}
      onSubmit={submit}
      footerNote={mySub ? t('demoRequests.followUp.assignedToYou') : undefined}
      data-testid="demo-followup-dialog"
    >
      <div data-testid="demo-followup-preset">
        <ChipSelector
          label={t('demoRequests.followUp.titleLabel')}
          value={preset}
          onChange={(v) => setPreset(v || FOLLOW_UP_PRESETS[0])}
          options={[
            ...FOLLOW_UP_PRESETS.map((p) => ({ value: p, label: t(followUpPresetKey(p)) })),
            { value: CUSTOM, label: t('demoRequests.followUp.presets.custom') },
          ]}
          disabled={pending}
        />
      </div>
      {preset === CUSTOM ? (
        <TextInput
          label={t('demoRequests.followUp.customTitle')}
          value={custom}
          onChange={(e) => setCustom(e.target?.value ?? e)}
          onBlur={() => setTouched(true)}
          maxLength={FOLLOW_UP_TITLE_MAX}
          error={titleError}
          disabled={pending}
          data-testid="demo-followup-custom"
        />
      ) : null}

      <FormField label={t('demoRequests.followUp.due')} error={dueError} helperText={t('demoRequests.followUp.dueHelp')}>
        <div data-testid="demo-followup-due">
          <JalaliDatePicker
            value={due}
            onChange={(v) => { setDue(v || ''); setTouched(true); }}
            withTime
            placeholder={t('demoRequests.followUp.duePlaceholder')}
            disabled={pending}
          />
        </div>
      </FormField>

      <Textarea
        label={t('demoRequests.followUp.note')}
        value={note}
        onChange={(e) => setNote(e.target?.value ?? e)}
        maxLength={NOTE_MAX}
        showCount
        rows={3}
        disabled={pending}
        data-testid="demo-followup-note"
      />
    </TaskDialog>
  );
}
